import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Alert, Presence } from '@boss/ui';

import { DynamicForm } from '..';
import { useSaveSubscription } from '../../client-queries';
import { FormType, useFormField } from '../../hooks';
import { buildFormFields } from '../../utils';
import { FormValues } from '../DynamicForm';
import { FormField } from '../Mappers/FormFieldMapper';

const FORM_FIELD_KEYS = ['email', 'termsandconditions'];

export type FormFieldKey = (typeof FORM_FIELD_KEYS)[number];

type Props = {
  type: FormType;
  className?: string;
};

const NewsLetterForm = ({ type, className }: Props) => {
  const fieldsToShow = [...FORM_FIELD_KEYS];
  const { t } = useTranslation('forms');

  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutate: saveSubscription, isLoading: isSubmitting } = useSaveSubscription();

  const { email, termsandconditions } = useFormField();

  const baseFields: FormField[] = [
    { ...email, colStyle: 'col-span-6' },
    { ...termsandconditions, colStyle: 'col-span-6' },
  ];

  const handleSubmit = (values: FormValues) => {
    setError(null);
    saveSubscription(
      {
        email: values.email as string,
        subscribeToNewsletter: true,
      },
      {
        onSuccess: () => {
          setIsSuccess(true);
          setError(null);
        },
        onError: error => {
          console.error(error);
          setError(t('errors.general'));
          setIsSuccess(false);
        },
      },
    );
  };

  const reset = () => {
    setIsSuccess(false);
    setError(null);
  };

  return (
    <Presence className={twMerge('flex flex-col gap-4', className)} id="newsletter-form-presence" visible>
      {error && (
        <Alert onClose={() => setError(null)} type="error">
          {error}
        </Alert>
      )}

      <DynamicForm
        buttonProps={{
          label: t('buttons.submitQuestion') ?? '',
        }}
        fields={buildFormFields(baseFields, fieldsToShow, [])}
        id={type}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        onCloseAlert={reset}
        onSubmit={handleSubmit}
        variant="light"
      />
    </Presence>
  );
};

export default NewsLetterForm;
